import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchMyCourses } from '../features/courses/courseApi';
import type { CourseSummary } from '../features/courses/courseApi';
import CompetitionStatusBadge from '../components/CompetitionStatusBadge';
import CourseChapterMenu from '../components/CourseChapterMenu';
import MenuShell from '../components/MenuShell';
import './CoursesPage.css';

/** Course selector (US6/FR-032): lists only courses the signed-in user can access. */
function CoursesPage() {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<CourseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadCourses() {
      try {
        const result = await fetchMyCourses();
        if (!ignore) {
          setCourses(result);
          setError(null);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(
            loadError instanceof Error ? loadError.message : 'Impossible de charger vos cours.',
          );
        }
      }
    }

    void loadCourses();
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      <MenuShell courseId="first" />
      <main className="courses-page">
        <header className="courses-page__hero">
          <h1>{t('coursesPage.title')}</h1>
          <p className="courses-page__tagline">{t('coursesPage.tagline')}</p>
        </header>

        {error && <p role="alert">{error}</p>}

        {courses && courses.length === 0 && (
          <p className="courses-page__empty">
            {t('coursesPage.empty')} <Link to="/exercise/first">{t('menu.challenge')}</Link>
          </p>
        )}

        {courses && courses.length > 0 && (
          <section className="courses-page__grid" aria-label={t('coursesPage.title')}>
            {courses.map((course) => (
              <article key={course.id} className="course-card">
                <span className="course-card__prompt" aria-hidden="true">
                  &gt;_
                </span>
                <h2 className="course-card__title">{course.title}</h2>
                <p className="course-card__description">{course.description}</p>
                <CompetitionStatusBadge courseId={course.id} />
                <CourseChapterMenu courseId={course.id} />
                <div className="course-card__links">
                  <Link to={`/courses/${course.id}`}>{t('coursesPage.viewExercises')}</Link>
                  <Link to={`/courses/${course.id}/progress`}>{t('menu.progress')}</Link>
                </div>
              </article>
            ))}
          </section>
        )}
      </main>
    </>
  );
}

export default CoursesPage;
